import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { Box, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { createAttachment } from "../../../../../../apis";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import { ModalContainer } from "../../components/ModalContainer";
import { InvestigationButton } from "../../components/InvestigationButton";
import { DragAndDropFiles, TemporalUploadedFile, UploadedFile } from "../../components/InvestigationComponents";

// Funcion revisar el formulario y retornar si es valido y cuales son los errores
const validateForm = (form) => {
  const { fileToUpload, typeFile } = form;
  const errors = {};
  if (fileToUpload == null) {
    errors.fileToUpload = true;
  }
  if (!typeFile) {
    errors.typeFile = true;
  }
  return [
    Object.entries(errors).length === 0 ? true : false,
    errors
  ];
}

// Modal de carga de preguntas para entrevistas
export const AddAttachments = ({ 
  show,
  onHide,
  idx,
  interviews,
  handleSaveInterviews,
  typeFileOpts,
  currentOptions = () => {},
}) => {

  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [fileToUpload, setFileToUpload] = useState(null);
  const [typeFile, setTypeFile] = useState("");

  const [triedToSave, setTriedToSave] = useState(false);
  const [errors, setErrors] = useState({});

  const selectTypeFileOpts = useMemo(() => {
    const filtredExistingAttachments = typeFileOpts.filter(opt => {
      const attachments = interviews[idx].attachments.principal || interviews[idx].attachments || [];
      const alreadyExists = attachments.some(att => att.purpose === opt.value);
      return !alreadyExists;
    });
    const formattedLabelForSelect = filtredExistingAttachments.map(opt => {
      return {
        label: `Carga de ${opt.label}`,
        value: opt.value,
      };
    });
    return formattedLabelForSelect;
  }, [show]);

  useEffect(() => {
    !show && resetValues();
  }, [show]);

  useEffect(() => {
    currentOptions(selectTypeFileOpts);
  }, [selectTypeFileOpts]);

  // Validar cada que se hace un cambio
  useEffect(() => {
    if (triedToSave) {
      const [_, errors] = validateForm({ fileToUpload, typeFile });
      setErrors(errors);
    }
  }, [fileToUpload, typeFile]);
  

  const handleAddAttachment = (file) => {
    setFileToUpload(file);
  }

  const handleSubmit = async() => {
    const [isValid, errors] = validateForm({ fileToUpload, typeFile });
    setErrors(errors);
    setTriedToSave(true);
    if (!isValid) {
      enqueueSnackbar({ variant: "error", message: t("resources.missing_required_inputs") });
      return;
    };

    const formData = new FormData();
    formData.append('attachments', fileToUpload);

    try {
      const resp = await createAttachment(formData);
      const fileId = resp.attachments_id;
      const newInterviews = _.cloneDeep(interviews);
      const newInterview = newInterviews[idx];
      const newFile = {
        file: fileId[0],
        name: fileToUpload.name,
        size: fileToUpload.size,
        type: fileToUpload.type,
        purpose: typeFile,
      };
      if (Array.isArray(newInterview.attachments)) {
        newInterview.attachments.push(newFile);
      } else if (Array.isArray(newInterview.attachments.principal)) { // Para compatibilidad con versiones anteriores
        newInterview.attachments.principal.push(newFile);
        console.log(newInterview);
      } else {
        newInterview.attachments = [newFile];
      }
      await handleSaveInterviews(newInterviews);
      enqueueSnackbar({ variant: "success", message: t("resources.successfully_saved") });
      resetValues();
      onHide();
    } catch (error) {
      console.log(error)
    }
  }

  const resetValues = () => {
    setFileToUpload(null);
    setTypeFile("");
    setTriedToSave(false);
    setErrors({});
  }

  const returnIsValid = (error) => {
    if (!triedToSave) return null;
    if (error) return false;
    return true;
  }

  return (
    <ModalContainer show={show} onHide={onHide}>

      {/* Header */}
      <Typography fontWeight={700} fontSize={28} color={theme.palette.ethicsBlue.main}>
        {t("Cargar archivos")}
      </Typography>
      <Typography fontSize={16} color="oxfordBlue" >
        {t("Carga archivos de soporte de entrevista")}
      </Typography>

      <hr />

      <>
        <Typography variant="body1" fontWeight={600} color="secondary">
          {t("Plantillas disponibles para archivos de soporte a entrevista")}
        </Typography>

        <Grid2 container spacing={2} mt={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <UploadedFile 
              text={t("Consentimiento de entrevista")}
              downloadUrl={"https://www.ethicsglobal.com/docs/es/Consentimiento_Entrevista_Investigacion.docx"}
              />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <UploadedFile
              text={t("Consentimiento de grabación de entrevista")}
              downloadUrl={"https://www.ethicsglobal.com/docs/es/Consentimiento_Grabacion_Entrevista.docx"}
            />
          </Grid2>
        </Grid2>

        <Box mt={2}>
          <DragAndDropFiles
            text={t("Arrastra y suelta tu plantilla")}
            onFileSelect={handleAddAttachment}
            isValid={returnIsValid(errors?.fileToUpload) ?? null}
          />
        </Box>

        {
          fileToUpload && 
          <Box mt={2}>
            <TemporalUploadedFile
              name={fileToUpload.name}
              size={fileToUpload.size}
              iconName="trash_can"
              iconAction={() => setFileToUpload(null)}
              iconColor={theme.palette.red.main}
            />
          </Box>
        }

        <Grid2 container >
          <Grid2 size={{ xs: 12, md: 6 }} mt={2}>
            <EGBasicSelect
              label={t("Tipo de archivo")}
              value={typeFile}
              onChange={(e) => setTypeFile(e.target.value)}
              options={selectTypeFileOpts}
              required={true}
              isValid={returnIsValid(errors?.typeFile) ?? null}
              showSuccessValidation
              showHelpText={false}
            />
          </Grid2>
        </Grid2>
      </>

      {/* Buttons */}
      <Stack 
        direction={{ xs: "column", sm: "row" }}
        spacing={2} 
        justifyContent={"flex-end"}
        mt={2}
      >
        <Stack direction="row" spacing={2}>
          <InvestigationButton
            label={t("CANCELAR")}            
            variant="main"
            outlined
            onClick={onHide}
          />
          <InvestigationButton
            label={t("CONFIRMAR")}
            variant="main"
            onClick={handleSubmit}
          />
        </Stack>
      </Stack>
    </ModalContainer>
  );
}